import React, { useState, memo } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircleIcon, ArrowRightIcon, MessageSquareIcon, CalendarIcon, CreditCardIcon, BarChart3Icon, UsersIcon, SmartphoneIcon, TrendingUpIcon, ChevronDownIcon, BuildingIcon, ZapIcon } from 'lucide-react';
import { ScrollReveal } from '../components/ScrollReveal';
import { SEOHead } from '../components/SEOHead';
export function CrmBusinessToolsPage() {
  const faqs = [{
    question: 'What features are included in the CRM?',
    answer: 'Our CRM includes a unified inbox for all communications (text, email, social, lead platforms), a drag-and-drop sales pipeline, automated appointment scheduling, invoicing and payment processing, and detailed reporting dashboards.'
  }, {
    question: 'Does it integrate with my lead generation efforts?',
    answer: 'Yes. Whether a lead comes from a paid ad, a lead platform message, a website form, or a direct phone call, it all funnels into one central dashboard. You never have to switch between apps to reply to customers.'
  }, {
    question: 'Can I process payments through the CRM?',
    answer: 'Yes. You can send professional, branded invoices via text or email and collect payments instantly using Stripe, Square, or PayPal integrations.'
  }, {
    question: 'Is there a mobile app?',
    answer: 'Yes. You and your team can download our mobile app (iOS and Android) to manage conversations, view your calendar, and send invoices directly from the field.'
  }, {
    question: 'How hard is it to migrate from my current system?',
    answer: 'We handle the heavy lifting. We can import your existing contacts and set up your pipelines so you can hit the ground running without losing historical data.'
  }, {
    question: 'How can I organize my leads if I do not have an office or admin staff?',
    answer: 'This is exactly what our CRM is built for. Every lead - whether it comes from a phone call, text, web form, or lead platform - lands in one visual pipeline automatically, so a solo owner-operator can see exactly where every job stands without needing anyone answering phones in an office.'
  }];
  const FAQItem = ({
    question,
    answer
  }: {
    question: string;
    answer: string;
  }) => {
    const [isOpen, setIsOpen] = useState(false);
    return <div className="border-b border-gray-800 last:border-0" data-id="element-1452" data-mp="9i26tk1uo8sr">
        <button onClick={() => setIsOpen(!isOpen)} className="w-full py-6 flex items-center justify-between text-left focus:outline-none group" data-id="element-1453" data-mp="9ky373y1upf3">
          <span className="text-lg font-bold text-white group-hover:text-blue-500 transition-colors" data-id="element-1454" data-mp="3vxwkp4d8m9kj">
            {question}
          </span>
          <ChevronDownIcon className={`w-5 h-5 text-gray-400 transition-transform duration-300 flex-shrink-0 ml-4 ${isOpen ? 'rotate-180 text-blue-500' : ''}`} data-id="element-1455" data-mp="shptvyfx86t6" />
        </button>
        <AnimatePresence data-id="element-1456" data-mp="14rfer68oth1y">
          {isOpen && <motion.div initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} transition={{
          duration: 0.3,
          ease: 'easeInOut'
        }} className="overflow-hidden" data-id="element-1457" data-mp="14s7ach2abes8">
              <p className="pb-6 text-gray-400 leading-relaxed" data-id="element-1458" data-mp="1sikvroqd6fdu">{answer}</p>
            </motion.div>}
        </AnimatePresence>
      </div>;
  };
  return <div className="bg-viridia-black min-h-screen" data-id="element-1459" data-mp="22fzbtn8dl25m">
      <SEOHead title="CRM & Business Tools for Home Services | Phoenix | Viridia Analytics" description="Centralize your home service operations with our robust CRM platform in Phoenix. Manage leads, track pipelines, and integrate with our AI lead conversion system." canonical="/crm-business-tools" keywords="CRM for local business, business management tools, lead tracking software, automated invoicing, appointment scheduling software, Phoenix business tools, marketing agency phoenix, HVAC, painters, landscapers, contractors, window installers" faq={faqs} data-id="element-1460" data-mp="22hadnhi9fzu3" />

      {/* HERO SECTION */}
      <section className="relative pt-32 pb-20 lg:pt-48 lg:pb-32 overflow-hidden lg:min-h-[90vh] flex items-center" data-id="element-1461" data-mp="2g88b9ppk2qzn">
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0 pointer-events-none" data-id="element-1462" data-mp="2u7zxueu51fp9">
          <div className="absolute top-[-10%] right-[-10%] w-[50%] h-[50%] rounded-full bg-blue-500/10 blur-[120px] animate-pulse-slow" data-id="element-1463" data-mp="2q5wi3e0froy6"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full" data-id="element-1464" data-mp="9jd8q6f0tqaw">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8 items-center" data-id="element-1465" data-mp="5i02hv80nl3g">
            {/* Hero Content */}
            <div className="max-w-2xl" data-id="element-1466" data-mp="hyjbty1jfdul">
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5
            }} data-id="element-1467" data-mp="hzkjrtn9bk83">
                <div className="inline-flex items-center px-3 py-1 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-500 text-sm font-semibold tracking-wide mb-6" data-id="element-1468" data-mp="vqpp9cdb36qc">
                  SUPPORTING INFRASTRUCTURE
                </div>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-6 tracking-tight" data-id="element-1469" data-mp="qyxuepn4aeue">
                  Run Your Entire Business From{' '}
                  <span className="text-blue-500" data-id="element-1470" data-mp="1emz3bzo7jxac">One Place</span>
                </h1>
                <p className="text-lg text-gray-400 mb-8 leading-relaxed" data-id="element-1471" data-mp="1jhleq2uahpuf">
                  Stop jumping between disconnected apps. Manage leads, customer
                  conversations, bookings, invoicing, and revenue visibility
                  from a single, centralized command center that integrates
                  perfectly with your lead conversion system.
                </p>
                <div className="flex flex-col sm:flex-row gap-4" data-id="element-1472" data-mp="1fbg35lu1gv1v">
                  <Link to="/contact" className="inline-flex justify-center items-center px-8 py-4 rounded-lg bg-viridia text-white font-semibold hover:bg-viridia-dark transition-colors duration-300 shadow-[0_0_20px_rgba(16,185,129,0.2)]" data-id="element-1473" data-mp="1adod6xcyfj0b">
                    Book a Strategy Call
                  </Link>
                  <Link to="/pricing" className="inline-flex justify-center items-center px-8 py-4 rounded-lg bg-transparent border border-gray-600 text-white font-semibold hover:bg-white/5 transition-colors duration-300" data-id="element-1474" data-mp="rlvmxap9sbwa">
                    View Packages
                  </Link>
                </div>
              </motion.div>
            </div>

            {/* Hero Visual - Command Center */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.7,
            delay: 0.2
          }} className="relative hidden lg:block h-[500px]" data-id="element-1475" data-mp="3893dvp8hkv38">
              <div className="absolute inset-0 bg-gradient-to-tr from-viridia-surface to-gray-900 rounded-2xl border border-gray-800 shadow-2xl overflow-hidden p-6" data-id="element-1476" data-mp="2oj181fxwx4m8">
                {/* Header */}
                <div className="flex justify-between items-center mb-6 pb-4 border-b border-gray-800" data-id="element-1477" data-mp="1wswspvp0f7rv">
                  <div className="flex gap-2" data-id="element-1478" data-mp="20t709csga68u">
                    <div className="w-3 h-3 rounded-full bg-red-500/50" data-id="element-1479" data-mp="u75xb1m7bx5i"></div>
                    <div className="w-3 h-3 rounded-full bg-yellow-500/50" data-id="element-1480" data-mp="p8ma6xvvgfbk"></div>
                    <div className="w-3 h-3 rounded-full bg-green-500/50" data-id="element-1481" data-mp="papb4cykbsv2"></div>
                  </div>
                  <div className="text-sm font-medium text-gray-400" data-id="element-1482" data-mp="143si5qej2o7j">
                    Business Command Center
                  </div>
                </div>

                {/* Modular Panels */}
                <div className="grid grid-cols-3 gap-4 h-[calc(100%-4rem)]" data-id="element-1483" data-mp="1d4gveiw3kkjd">
                  {/* Left Col - Pipeline */}
                  <div className="col-span-1 space-y-4" data-id="element-1484" data-mp="1mw3gd6y8lxzb">
                    <div className="bg-viridia-black/50 rounded-xl p-4 border border-gray-800/50 h-1/2" data-id="element-1485" data-mp="1mvlarjpoq7eq">
                      <div className="text-xs text-gray-500 mb-3 uppercase tracking-wider font-bold" data-id="element-1486" data-mp="1hy1uyfdc9mko">
                        Active Pipeline
                      </div>
                      <div className="space-y-2" data-id="element-1487" data-mp="1rwhgm2tlk3ms">
                        <div className="h-8 bg-blue-500/10 rounded border border-blue-500/20 flex items-center px-3" data-id="element-1488" data-mp="2ajqjvr2xgioa">
                          <span className="text-xs text-blue-400" data-id="element-1489" data-mp="2fjcx40ir9yx3">
                            New Leads (4)
                          </span>
                        </div>
                        <div className="h-8 bg-yellow-500/10 rounded border border-yellow-500/20 flex items-center px-3" data-id="element-1490" data-mp="21lejixc9e6in">
                          <span className="text-xs text-yellow-400" data-id="element-1491" data-mp="1rtiknk2cmcdd">
                            Estimates Sent (2)
                          </span>
                        </div>
                        <div className="h-8 bg-viridia/10 rounded border border-viridia/20 flex items-center px-3" data-id="element-1492" data-mp="1wltmwtkkaa93">
                          <span className="text-xs text-viridia" data-id="element-1493" data-mp="1mtw7tbqfmy53">
                            Jobs Won (5)
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="bg-viridia-black/50 rounded-xl p-4 border border-gray-800/50 h-1/2" data-id="element-1494" data-mp="1dtr1z60w07t1">
                      <div className="text-xs text-gray-500 mb-3 uppercase tracking-wider font-bold" data-id="element-1495" data-mp="30u1ji404ju30">
                        Recent Comms
                      </div>
                      <div className="space-y-3" data-id="element-1496" data-mp="2qx6mxqnew3rd">
                        <div className="flex gap-2 items-center" data-id="element-1497" data-mp="2qyhpjn54f5gw">
                          <div className="w-6 h-6 rounded-full bg-gray-800 flex-shrink-0" data-id="element-1498" data-mp="2vv8589d0jz1s"></div>
                          <div className="h-2 bg-gray-700 rounded w-full" data-id="element-1499" data-mp="3jl2jmvczdvo5"></div>
                        </div>
                        <div className="flex gap-2 items-center" data-id="element-1500" data-mp="3okymusdutj2n">
                          <div className="w-6 h-6 rounded-full bg-gray-800 flex-shrink-0" data-id="element-1501" data-mp="39vsnn4f0nsur"></div>
                          <div className="h-2 bg-gray-700 rounded w-3/4" data-id="element-1502" data-mp="3euvw6yu7gh7h"></div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Middle Col - Calendar & Revenue */}
                  <div className="col-span-2 space-y-4" data-id="element-1503" data-mp="b6u5gsb7h7fi">
                    <div className="bg-viridia-black/50 rounded-xl p-4 border border-gray-800/50 h-2/3 relative overflow-hidden" data-id="element-1504" data-mp="1vw7o9xdudil7">
                      <div className="absolute top-0 right-0 w-32 h-32 bg-viridia/10 rounded-full blur-2xl" data-id="element-1505" data-mp="1qxwf7ovdfc41"></div>
                      <div className="text-xs text-gray-500 mb-4 uppercase tracking-wider font-bold" data-id="element-1506" data-mp="24n7zmna28bur">
                        Revenue Snapshot
                      </div>
                      <div className="text-3xl font-bold text-white mb-1" data-id="element-1507" data-mp="1cwtq1392xlow">
                        $24,850
                      </div>
                      <div className="text-xs text-viridia flex items-center mb-6" data-id="element-1508" data-mp="17zk25s1c9i36">
                        <TrendingUpIcon className="w-3 h-3 mr-1" data-id="element-1509" data-mp="1lz2yzoop9706" /> +12% this
                        month
                      </div>
                      {/* Fake Chart */}
                      <div className="flex items-end gap-2 h-24 mt-auto" data-id="element-1510" data-mp="2xcnmcab37t6i">
                        {[40, 60, 45, 80, 65, 90, 75].map((h, i) => <motion.div key={i} initial={{
                        height: 0
                      }} animate={{
                        height: `${h}%`
                      }} transition={{
                        duration: 1,
                        delay: 0.5 + i * 0.1
                      }} className="flex-1 bg-gradient-to-t from-viridia/20 to-viridia/60 rounded-t-sm" data-id="element-1511" data-mp="2sdkjmwikxz1p" data-mpi={i}></motion.div>)}
                      </div>
                    </div>
                    <div className="bg-viridia-black/50 rounded-xl p-4 border border-gray-800/50 h-1/3 flex items-center justify-between" data-id="element-1512" data-mp="1g9biey9wuh0n">
                      <div data-id="element-1513" data-mp="nm76088hn0eu">
                        <div className="text-xs text-gray-500 mb-1 uppercase tracking-wider font-bold" data-id="element-1514" data-mp="nmz2rmdtn46p">
                          Next Appointment
                        </div>
                        <div className="text-sm font-medium text-white" data-id="element-1515" data-mp="l1iwuef8hle8">
                          AC Repair Estimate
                        </div>
                        <div className="text-xs text-gray-400" data-id="element-1516" data-mp="g37pqbgbpozp">
                          Today, 2:00 PM
                        </div>
                      </div>
                      <div className="w-10 h-10 rounded-full bg-viridia/10 flex items-center justify-center" data-id="element-1517" data-mp="72jajdf71il8">
                        <CalendarIcon className="w-5 h-5 text-viridia" data-id="element-1518" data-mp="236r72nj6tne" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CORE FEATURES */}
      <section className="py-24 bg-viridia-surface border-t border-gray-800" data-id="element-1519" data-mp="3jjwx1znqd2qf">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1520" data-mp="3fhtiv39ofk1g">
          <ScrollReveal data-id="element-1521" data-mp="3fh09dujc2gfm">
            <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1522" data-mp="3fjmfrquhmpw9">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-1523" data-mp="3aj8hh0iouubf">
                Everything You Need to Operate
              </h2>
              <p className="text-xl text-gray-400" data-id="element-1524" data-mp="1igxgakwhu4xg">
                A CRM built specifically to support local service businesses,
                keeping your pipeline organized and your follow-up consistent.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-1525" data-mp="19mr4jjm74ncv">
            {[{
            icon: <MessageSquareIcon className="w-6 h-6 text-white" data-id="element-1526" data-mp="zqo5immn54u5" />,
            title: 'Unified Inbox',
            desc: 'See texts, emails, social DMs, and lead platform messages in one single thread per customer.'
          }, {
            icon: <BarChart3Icon className="w-6 h-6 text-white" data-id="element-1527" data-mp="qq9h3eedr9mf" />,
            title: 'Visual Sales Pipeline',
            desc: 'Drag and drop leads through stages (New Lead → Estimate Sent → Job Won) so you always know where money is sitting.'
          }, {
            icon: <CalendarIcon className="w-6 h-6 text-white" data-id="element-1528" data-mp="2v8gzyofooccp" />,
            title: 'Automated Scheduling',
            desc: 'Let customers book directly on your calendar based on your real-time availability. No more phone tag.'
          }, {
            icon: <CreditCardIcon className="w-6 h-6 text-white" data-id="element-1529" data-mp="2z8hqh7g24e04" />,
            title: 'Invoicing & Payments',
            desc: 'Send professional invoices via text or email and get paid faster with Stripe, Square, or PayPal integration.'
          }, {
            icon: <UsersIcon className="w-6 h-6 text-white" data-id="element-1530" data-mp="3mzn2tcmsgo5o" />,
            title: 'Contact Management',
            desc: 'Store every detail, past conversation, and job history for every customer in a secure, searchable database.'
          }, {
            icon: <SmartphoneIcon className="w-6 h-6 text-white" data-id="element-1531" data-mp="1ojilvka2dybc" />,
            title: 'Mobile App',
            desc: 'Run your business from the truck. Reply to leads, check your schedule, and send invoices from your phone.'
          }].map((feature, i) => <ScrollReveal key={i} delay={i * 0.1} data-id="element-1532" data-mp="3sp6l8p6tvtke" data-mpi={i}>
                <div className="bg-viridia-black rounded-2xl p-8 border border-gray-800 h-full hover:border-viridia/50 transition-colors" data-id="element-1533" data-mp="1g0yyzazd3yl">
                  <div className="w-12 h-12 rounded-xl bg-gray-800 flex items-center justify-center mb-6" data-id="element-1534" data-mp="6dsm7g0ok1wg">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3" data-id="element-1535" data-mp="ffinddkd6f7f">
                    {feature.title}
                  </h3>
                  <p className="text-gray-400 leading-relaxed" data-id="element-1536" data-mp="k9mv3z1ger9k">
                    {feature.desc}
                  </p>
                </div>
              </ScrollReveal>)}
          </div>
        </div>
      </section>

      {/* PIPELINE VISUALIZATION */}
      <section className="py-24 bg-viridia-black" data-id="element-1537" data-mp="y84g3ozz5pns">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1538" data-mp="2ipdole8d8v8a">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-1539" data-mp="2iqoq17ec9qw8">
            <ScrollReveal data-id="element-1540" data-mp="2emz64v8lzxhj">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-1541" data-mp="2epv2if4t6e3v">
                Stop Losing Track of Leads
              </h2>
              <p className="text-lg text-gray-400 mb-6 leading-relaxed" data-id="element-1542" data-mp="24q45o6srzn0m">
                When you rely on sticky notes, whiteboards, or your memory,
                leads fall through the cracks. Our CRM gives you a clear, visual
                pipeline of exactly where every potential job stands.
              </p>
              <ul className="space-y-4 mb-8" data-id="element-1543" data-mp="2smi08k4l58au">
                <li className="flex items-start" data-id="element-1544" data-mp="2sl6wulf1y2k9">
                  <CheckCircleIcon className="w-6 h-6 text-viridia mr-4 flex-shrink-0" data-id="element-1545" data-mp="2nnf6vwxywqip" />
                  <span className="text-gray-300" data-id="element-1546" data-mp="3l4441dbz34lr">
                    Know exactly who needs an estimate sent
                  </span>
                </li>
                <li className="flex items-start" data-id="element-1547" data-mp="3gbazoqkf1ya7">
                  <CheckCircleIcon className="w-6 h-6 text-viridia mr-4 flex-shrink-0" data-id="element-1548" data-mp="2oohzerij339w" />
                  <span className="text-gray-300" data-id="element-1549" data-mp="2tgj7e18ny7u4">
                    See which estimates are waiting for approval
                  </span>
                </li>
                <li className="flex items-start" data-id="element-1550" data-mp="2fn1q6n3g9779">
                  <CheckCircleIcon className="w-6 h-6 text-viridia mr-4 flex-shrink-0" data-id="element-1551" data-mp="2jrhsyfhrnyi9" />
                  <span className="text-gray-300" data-id="element-1552" data-mp="37fsp55cipa07">
                    Track total potential revenue in your pipeline
                  </span>
                </li>
              </ul>
              <Link to="/contact" className="inline-flex items-center text-viridia font-bold hover:text-viridia-dark transition-colors text-lg" data-id="element-1553" data-mp="2yfmbnjgtka11">
                Get Organized Today <ArrowRightIcon className="ml-2 w-5 h-5" data-id="element-1554" data-mp="1mzxqmxn4ll2v" />
              </Link>
            </ScrollReveal>

            <ScrollReveal direction="left" data-id="element-1555" data-mp="1rzk1x1je8r91">
              <div className="bg-viridia-surface rounded-2xl p-6 border border-gray-800 shadow-2xl" data-id="element-1556" data-mp="1rx7e47qyfmo2">
                <div className="flex justify-between items-center mb-6" data-id="element-1557" data-mp="1jkcmvhjoy7wf">
                  <h3 className="text-white font-bold" data-id="element-1558" data-mp="1fg4y5kd8gry7">Sales Pipeline</h3>
                  <span className="text-viridia text-sm font-medium" data-id="element-1559" data-mp="1tgpf0azl8dp2">
                    $12,400 Potential
                  </span>
                </div>
                <div className="space-y-4" data-id="element-1560" data-mp="1ofjo8iphn0i6">
                  {/* Stage 1 */}
                  <div data-id="element-1561" data-mp="10s1skc33ri9x">
                    <div className="flex justify-between text-sm mb-2" data-id="element-1562" data-mp="10pfo4lbidivx">
                      <span className="text-gray-400 font-medium" data-id="element-1563" data-mp="vuiepdabh7ii">
                        New Leads (3)
                      </span>
                      <span className="text-gray-500" data-id="element-1564" data-mp="15qv3bwr08v6e">$2,100</span>
                    </div>
                    <div className="space-y-2" data-id="element-1565" data-mp="i1alxiai25z8">
                      <div className="bg-viridia-black p-3 rounded border border-gray-800 text-sm text-gray-300" data-id="element-1566" data-mp="hzzjblssj49p">
                        Sarah M. - AC Repair
                      </div>
                      <div className="bg-viridia-black p-3 rounded border border-gray-800 text-sm text-gray-300" data-id="element-1567" data-mp="35mm7dsyx314j">
                        Mike T. - Maintenance
                      </div>
                    </div>
                  </div>
                  {/* Stage 2 */}
                  <div data-id="element-1568" data-mp="3ams6fu7inpwn">
                    <div className="flex justify-between text-sm mb-2" data-id="element-1569" data-mp="3am8l03inqjdv">
                      <span className="text-blue-400 font-medium" data-id="element-1570" data-mp="3odn6qi3y8yom">
                        Estimate Sent (2)
                      </span>
                      <span className="text-gray-500" data-id="element-1571" data-mp="3jdsjcojdur8b">$4,800</span>
                    </div>
                    <div className="space-y-2" data-id="element-1572" data-mp="25nlddbx12nf">
                      <div className="bg-viridia-black p-3 rounded border border-blue-900/30 text-sm text-gray-300" data-id="element-1573" data-mp="24cjjj2164yy">
                        David L. - Full Install
                      </div>
                    </div>
                  </div>
                  {/* Stage 3 */}
                  <div data-id="element-1574" data-mp="1z0jsshksnjtq">
                    <div className="flex justify-between text-sm mb-2" data-id="element-1575" data-mp="1z1bnzrahbfjh">
                      <span className="text-viridia font-medium" data-id="element-1576" data-mp="ehg5zwvmzke4">
                        Job Won (4)
                      </span>
                      <span className="text-gray-500" data-id="element-1577" data-mp="4r4spjq2byuk">$5,500</span>
                    </div>
                    <div className="space-y-2" data-id="element-1578" data-mp="4pahix4lfl3o">
                      <div className="bg-viridia-black p-3 rounded border border-viridia/30 text-sm text-gray-300" data-id="element-1579" data-mp="9p6be3jktw5i">
                        Jessica R. - Duct Cleaning
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Best Fit Section */}
      <section className="py-20 bg-viridia-surface border-t border-gray-800" data-id="element-1580" data-mp="3mw84toeg5b4h">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1581" data-mp="1g6a6birqfhsy">
          <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-1582" data-mp="1g735nyod3512">
            <ScrollReveal data-id="element-1583" data-mp="1l4bswdod9z96">
              <h2 className="text-3xl font-bold text-white mb-6" data-id="element-1584" data-mp="1l4lhpxxoe0da">
                Who is this for?
              </h2>
              <p className="text-gray-400 mb-6 leading-relaxed" data-id="element-1585" data-mp="1jluzk4xjjcl5">
                Our CRM is designed as the supporting infrastructure for local
                service businesses that want to centralize their operations.
              </p>
              <ul className="space-y-4" data-id="element-1586" data-mp="14rqfwikwvmou">
                <li className="flex items-start gap-3" data-id="element-1587" data-mp="14srqkly0m761">
                  <BuildingIcon className="w-6 h-6 text-blue-500 flex-shrink-0 mt-0.5" data-id="element-1588" data-mp="22eywq09yw7ki" />
                  <div data-id="element-1589" data-mp="1xf2rxytg4c3w">
                    <h4 className="font-bold text-white" data-id="element-1590" data-mp="1xgxcvd4pw47z">
                      Disorganized Contractors
                    </h4>
                    <p className="text-sm text-gray-400" data-id="element-1591" data-mp="1smt4cpfszo4s">
                      If you are using sticky notes, whiteboards, or multiple
                      apps to track jobs, you need a centralized CRM.
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-3" data-id="element-1592" data-mp="2gdotfesvk58c">
                  <ZapIcon className="w-6 h-6 text-blue-500 flex-shrink-0 mt-0.5" data-id="element-1593" data-mp="2one0n46d98b4" />
                  <div data-id="element-1594" data-mp="2opgyubhoa7qx">
                    <h4 className="font-bold text-white" data-id="element-1595" data-mp="2so6de7eg5vc5">
                      Businesses Using Our AI
                    </h4>
                    <p className="text-sm text-gray-400" data-id="element-1596" data-mp="2xn16c264p09z">
                      The CRM is the perfect companion to our AI Lead Conversion
                      system, giving you full visibility into every lead the AI
                      captures.
                    </p>
                  </div>
                </li>
              </ul>
            </ScrollReveal>
            <ScrollReveal delay={0.2} data-id="element-1597" data-mp="3gek8k6sbwaay">
              <div className="bg-viridia-black border border-gray-800 rounded-2xl p-8" data-id="element-1598" data-mp="3gfllkgswn6vb">
                <h3 className="text-xl font-bold text-white mb-4" data-id="element-1599" data-mp="3lcc0h0sz3wf5">
                  Related Services
                </h3>
                <div className="space-y-4" data-id="element-1600" data-mp="3qeb328gw2pb1">
                  <Link to="/ai-lead-conversion" className="block p-4 bg-viridia-surface rounded-xl border border-gray-800 hover:border-viridia/50 transition-colors" data-id="element-1601" data-mp="3vbb5m9ej59xe">
                    <div className="font-bold text-white mb-1" data-id="element-1602" data-mp="qxfqgr4dm8be">
                      AI Lead Conversion
                    </div>
                    <div className="text-sm text-gray-400" data-id="element-1603" data-mp="15mka8cqtkklm">
                      Our flagship system for instant lead response and booking.
                    </div>
                  </Link>
                  <Link to="/custom-automations" className="block p-4 bg-viridia-surface rounded-xl border border-gray-800 hover:border-viridia/50 transition-colors" data-id="element-1604" data-mp="cyy4oyy175w4">
                    <div className="font-bold text-white mb-1" data-id="element-1605" data-mp="lx1h95r1gsbs">
                      Custom Automations
                    </div>
                    <div className="text-sm text-gray-400" data-id="element-1606" data-mp="1xdrpsvixpw8k">
                      Bespoke workflows to connect your CRM with other tools.
                    </div>
                  </Link>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* FAQ SECTION */}
      <section className="py-24 bg-viridia-black border-t border-gray-800" data-id="element-1607" data-mp="25wbggp9ghofn">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center mb-16" data-id="element-1608" data-mp="1qz2n5q0leala">
          <ScrollReveal data-id="element-1609" data-mp="1vx486ghkwjze">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6" data-id="element-1610" data-mp="1vyf6i0s98zj4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-400" data-id="element-1611" data-mp="2jp1gvypqevwa">
              Everything you need to know about our CRM and business tools.
            </p>
          </ScrollReveal>
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1612" data-mp="29wlyx1efq7be">
          <ScrollReveal delay={0.2} data-id="element-1613" data-mp="2evq47gy8nkwg">
            <div className="bg-viridia-surface border border-gray-800 rounded-2xl p-6 md:p-8" data-id="element-1614" data-mp="2eunhzmbv26mv">
              {faqs.map((faq, index) => <FAQItem key={index} question={faq.question} answer={faq.answer} data-id="element-1615" data-mp="37cj1996o2rp8" data-mpi={index} />)}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="py-24 bg-viridia-surface border-t border-gray-800" data-id="element-1616" data-mp="3ca7wpd7k7jak">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1617" data-mp="386ib8wm6lhtr">
          <ScrollReveal data-id="element-1618" data-mp="339jjultwjoz9">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6" data-id="element-1619" data-mp="337yqv0n8ke3i">
              Ready to get organized?
            </h2>
            <p className="text-xl text-gray-400 mb-10 max-w-2xl mx-auto" data-id="element-1620" data-mp="2ybi1620e1rob">
              Book a strategy call to see how our CRM can streamline your
              operations and help you close more jobs.
            </p>
            <Link to="/contact" className="inline-flex items-center justify-center px-8 py-4 text-lg font-bold rounded-lg bg-viridia text-white hover:bg-viridia-dark transition-all duration-300 shadow-[0_0_20px_rgba(16,185,129,0.2)]" data-id="element-1621" data-mp="2kiim214ipzha">
              Book a Strategy Call <ArrowRightIcon className="w-5 h-5 ml-2" data-id="element-1622" data-mp="q2agzromyoq5" />
            </Link>
          </ScrollReveal>
        </div>
      </section>
    </div>;
}